import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TradingAccountDepositFormValues } from '@pages/Traders-Room/Trading-Accounts/Deposit/TradingAccountDepositForm/TradingAccountDepositForm'

import { BaseModalType } from '@context/ModalsContext'

import { FeatureIcon } from '@ui/components/FeatureIcon/FeatureIcon'
import { Modal } from '@ui/components/Modal'
import { AlertIcon } from '@ui/icons/Alert.icon'
import { ExternalLinkIcon } from '@ui/icons/ExternalLink.icon'

import { getCurrencySymbol } from '@utils/transaction.utils'

import type { WalletDepositFormValues } from '@model/WalletDepositFormValues'
import { CurrencyType } from '@model/WalletDto'

interface CardPaymentProviderModalProps {
  onProceed(): void
  onClose(): void
  values: WalletDepositFormValues | TradingAccountDepositFormValues
}

type FormValues = WalletDepositFormValues | TradingAccountDepositFormValues

const isWalletDepositFormValues = (values: FormValues): values is WalletDepositFormValues =>
  (values as WalletDepositFormValues).wallet !== undefined

const isTradingAccountDepositFormValues = (
  values: FormValues
): values is TradingAccountDepositFormValues =>
  (values as TradingAccountDepositFormValues).tradingAccount !== undefined

export const CardPaymentProviderProceedModal: BaseModalType<CardPaymentProviderModalProps> = ({
  onProceed,
  values,
  close,
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  if (!values) {
    return null
  }

  const handleProceed = async () => {
    setIsLoading(true)
    try {
      await Promise.resolve(onProceed())
    } finally {
      close()
    }
  }

  const currency = isWalletDepositFormValues(values)
    ? (values.wallet.currency.id as CurrencyType)
    : isTradingAccountDepositFormValues(values)
    ? (values.tradingAccount.currency.id as CurrencyType)
    : undefined

  const currencySymbol = currency ? getCurrencySymbol(currency) : ''
  const title = t('Deposit of')

  return (
    <Modal>
      <Modal.Body centered>
        <FeatureIcon size='lg' icon={AlertIcon} bg='surface-orange-high-em' />
        <Modal.Body.Title color='text-high-em'>
          {`${title} ${currencySymbol}${values.amount}`}
        </Modal.Body.Title>
        <Modal.Body.Text>
          {t('You will be redirected to an external payment provider to complete your transaction')}
        </Modal.Body.Text>
        <Modal.Body.Text>{t('Card payment redirection ownership notice')}</Modal.Body.Text>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Primary
          iconRight={ExternalLinkIcon}
          onClick={handleProceed}
          disabled={isLoading}
        >
          {t('Proceed')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
